import React, { useCallback, useEffect, useState } from "react";
import "../../../App.css";

// import TelegramHeader from "../../../components/kit/Header/TelegramHeader";
import TelegramText from "../../../components/kit/Text/TelegramText";
import { useTelegram } from "../../../hooks/useTelegram";
import { useNavigate } from "react-router-dom";
import {
  PATH_BRAND,
  PATH_CHECKOUT,
  PATH_HOME,
  PATH_ORDER_REVIEW,
  PATH_STORE_SELECTION,
} from "../../../constants/Paths";
import TelegramScreen from "../../../components/kit/Screen/TelegramScreen";
import ItemButton from "../../../components/kit/ItemButton/ItemButton";
import TelegramHeader from "../../../components/kit/Header/TelegramHeader";
import StoreIndicator from "../../../components/kit/StoreIndicator/StoreIndicator";
import { useDispatch, useSelector } from "react-redux";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlags";
import { bypassToAquilaStore, shouldBypassStoreSelection } from "../../../utils/storeBypassHandler";
import { replaceCart } from "../../../components/kit/Redux/Actions";
import { useAlert } from "../../../components/providers/AlertProvider";
import ItemSkeleton from "../../../components/kit/Skeleton/ItemSkeleton";
// import Cart from "../../../components/kit/Cart/Cart";

const { getBrandByType, getProductTypesByStore } = require("../../../db/db");

const ProductTypeScreen = () => {
  const { webApp } = useTelegram();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useAlert();

  const [storeProductTypes, setStoreProductTypes] = useState([]); // Store-specific product types
  const [hasLoadedProductTypes, setHasLoadedProductTypes] = useState(false); // Track if we've loaded product types

  const productTypes = useSelector((state) => state.productTypeList); // Legacy global product types (fallback)
  const sessionToken = useSelector((state) => state.sessionToken);
  const userProfile = useSelector((state) => state.userProfile);
  const cartItems = useSelector((state) => state.cartItems.cartItems);
  const existingOrder = useSelector((state) => state.existingOrder);
  const currentSelectedStore = useSelector((state) => state.selectedStore);

  const onMainClick = useCallback(
    (selectedProductType) => {
      // Optimistic navigation: Update state and navigate immediately
      dispatch({
        type: "UPDATE_SELECTED_PRODUCT_TYPE",
        payload: selectedProductType,
      });
      dispatch({ type: "UPDATE_BRAND_LIST", payload: [] }); // Empty array shows skeleton

      navigate(PATH_BRAND, {
        replace: true,
      });

      // Load data in background
      getBrandByType(
        sessionToken,
        selectedProductType.title,
        currentSelectedStore?.storeId
      )
        .then((data) => {
          dispatch({ type: "UPDATE_BRAND_LIST", payload: data });
        })
        .catch((error) => {
          // Check if this is a session invalidation error
          if (error.message && error.message.includes('Access revoked')) {
            // Session invalidation is already handled by postToAPI, just wait
            return;
          }

          triggerAlert(
            "Loading Error",
            "Failed to load brands for this world. Please try again."
          );
        });
    },
    [
      navigate,
      dispatch,
      sessionToken,
      currentSelectedStore?.storeId,
      triggerAlert,
    ]
  );

  const onCartClick = useCallback(() => {
    const sortedCartItems = [...cartItems].sort((a, b) => {
      // Sort by Type
      const typeResult = a.type.localeCompare(b.type);
      if (typeResult !== 0) return typeResult;

      // Sort by Brand
      const brandResult = a.brand.localeCompare(b.brand);
      if (brandResult !== 0) return brandResult;

      // Sort by Category
      const categoryResult = a.category.localeCompare(b.category);
      if (categoryResult !== 0) return categoryResult;

      // Sort by Title
      return a.title.localeCompare(b.title);
    });

    if (existingOrder) {
      navigate(PATH_ORDER_REVIEW, { replace: true });
    } else {
      navigate(PATH_CHECKOUT, { replace: true });
    }
  }, [navigate, cartItems, dispatch]);

  // Clear cached brand and category data when store changes
  useEffect(() => {
    dispatch({ type: "UPDATE_BRAND_LIST", payload: [] });
    dispatch({ type: "UPDATE_CATEGORY_LIST", payload: [] });
    dispatch({ type: "UPDATE_PRODUCT_LIST", payload: [] });
  }, [currentSelectedStore?.storeId, dispatch]);

  // Lazy load store-specific product types when store is selected
  useEffect(() => {
    const fetchStoreProductTypes = async () => {
      if (currentSelectedStore?.storeId && sessionToken) {
        try {
          console.log(
            `Fetching product types for store: ${currentSelectedStore.storeName} (ID: ${currentSelectedStore.storeId})`
          );

          const storeSpecificProductTypes = await getProductTypesByStore(
            sessionToken,
            currentSelectedStore.storeId
          );

          // alert(
          //   `🎯 Setting ${storeSpecificProductTypes.length} product types for ${currentSelectedStore.storeName}`
          // );
          setStoreProductTypes(storeSpecificProductTypes);
          setHasLoadedProductTypes(true);
        } catch (error) {
          // Check if this is a session invalidation error
          if (error.message && error.message.includes('Access revoked')) {
            // Session invalidation is already handled by postToAPI, just wait
            setHasLoadedProductTypes(true);
            return;
          }
          
          // Fallback to empty array on error
          setStoreProductTypes([]);
          setHasLoadedProductTypes(true);
          triggerAlert(
            "Loading Error",
            "Failed to load world inventory. Please try again."
          );
        }
      } else {
        // Reset state when no store is selected
        setStoreProductTypes([]);
        setHasLoadedProductTypes(false);
      }
    };

    fetchStoreProductTypes();
  }, [currentSelectedStore?.storeId, sessionToken, triggerAlert]);

  useEffect(() => {
    // Check if user has selected a store, if not redirect to store selection
    if (!currentSelectedStore || !currentSelectedStore.storeName) {
      console.log(
        "ProductTypeScreen - No store selected"
      );
      
      // Check if store bypass is enabled
      if (shouldBypassStoreSelection(FEATURE_FLAGS.ENABLE_STORE_BYPASS)) {
        console.log("ProductTypeScreen - Store bypass enabled, auto-selecting Aquila");
        
        if (sessionToken && userProfile) {
          bypassToAquilaStore(sessionToken, userProfile, dispatch, navigate)
            .catch((bypassError) => {
              console.error('ProductTypeScreen - Store bypass failed, falling back to store selection:', bypassError);
              navigate(PATH_STORE_SELECTION, { replace: true });
            });
        } else {
          console.log("ProductTypeScreen - Missing session/user data, redirecting to store selection");
          navigate(PATH_STORE_SELECTION, { replace: true });
        }
      } else {
        console.log("ProductTypeScreen - Store bypass disabled, redirecting to store selection");
        navigate(PATH_STORE_SELECTION, { replace: true });
      }
      return;
    }
    webApp.ready();
    webApp.MainButton.onClick(onCartClick);

    webApp.BackButton.hide();

    if (cartItems.length > 0) {
      webApp.MainButton.text = "VIEW ORDER";
      webApp.MainButton.color = "#31b545";
      webApp.MainButton.show();
    } else {
      webApp.MainButton.hide();
    }

    return () => {
      webApp.MainButton.offClick(onCartClick);
    };
  }, [
    webApp,
    onMainClick,
    onCartClick,
    cartItems,
    navigate,
    triggerAlert,
    currentSelectedStore,
  ]);

  // Determine which product types to display (store-specific or fallback)
  const displayProductTypes = hasLoadedProductTypes
    ? storeProductTypes
    : productTypes || [];
  const showEmptyState =
    hasLoadedProductTypes && storeProductTypes.length === 0;

  // Debug UI state
  // if (hasLoadedProductTypes) {
  //   alert(`🎬 UI State: hasLoaded=${hasLoadedProductTypes}, storeTypes=${storeProductTypes.length}, display=${displayProductTypes.length}, empty=${showEmptyState}`);
  // }

  return (
    <TelegramScreen>
      <StoreIndicator />
      <div>
        {displayProductTypes?.length > 0 ? (
          <>
            <TelegramHeader>
              <TelegramText className={"telegramTitle"}>TYPE</TelegramText>
            </TelegramHeader>
            <div className="oneline__cards__container">
              {displayProductTypes.map((item) => {
                return (
                  <ItemButton
                    item={item}
                    key={item.title}
                    handleButtonClick={onMainClick}
                    type="type"
                  />
                );
              })}
            </div>
          </>
        ) : showEmptyState ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
              textAlign: "center",
            }}
          >
            <TelegramHeader>
              <TelegramText className={"telegramTitle"}>
                This world has no adventurers.
              </TelegramText>
              <TelegramText
                className={"telegramSubtitle"}
                style={{ textAlign: "center" }}
              >
                No items dropped in{" "}
                {currentSelectedStore?.storeName || "this world"}.
              </TelegramText>
            </TelegramHeader>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <button
                style={{
                  backgroundColor: "var(--tg-theme-button-color)",
                  color: "var(--tg-theme-button-text-color)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(PATH_STORE_SELECTION, { replace: true })
                }
              >
                Back to World Selection
              </button>
            </div>
          </div>
        ) : (
          <>
            <TelegramHeader>
              <TelegramText className={"telegramTitle"}>TYPE</TelegramText>
            </TelegramHeader>
            <div className="oneline__cards__container">
              <ItemSkeleton count={3} />
            </div>
          </>
        )}
      </div>
    </TelegramScreen>
  );
};

export default ProductTypeScreen;
